"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthClient = void 0;
const tslib_1 = require("tslib");
const cookie_container_1 = require("../cookie.container");
const errors_1 = require("../errors");
const logout_result_model_1 = require("../models/logout.result.model");
const base_client_1 = require("./base.client");
const token_storage_1 = require("../token.storage");
const REFRESH_TOKEN = 'refreshtoken';
class AuthClient extends base_client_1.BaseClient {
    constructor() {
        super(...arguments);
        this.baseRoute = '/v1/auth';
    }
    login(auth) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const opts = { responseCookies: new cookie_container_1.CookieContainer(), throwOnError: false, withCredentials: true };
            const response = yield this.httpPost('/v1/auth', auth, opts).catch((err) => err);
            if (!response.success) {
                throw (0, errors_1.createAuthError)(response);
            }
            token_storage_1.TokenStorage.store(response.payload.jwtToken);
            const cookie = opts.responseCookies.get('refreshtoken') || {};
            return Object.assign(Object.assign({}, response.payload), { refreshTokenId: cookie.value });
        });
    }
    createJwtSsoToken(jwtToken) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!jwtToken) {
                throw new Error('JWT Token not provided.');
            }
            const response = yield this.httpGet('/v1/auth/jwt-sso', {
                withCredentials: true,
                headers: this.getHeaders({ jwtToken }),
                throwOnError: false,
            });
            if (!response.success) {
                throw new Error(response.errorMessage);
            }
            else {
                return response.payload;
            }
        });
    }
    createThirdPartyToken(jwtToken, serviceName) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!jwtToken) {
                throw new Error('JWT Token not provided.');
            }
            const queryParam = `?serviceName=${serviceName}`;
            const response = yield this.httpGet(`/v1/auth/jwt-sso${queryParam}`, {
                withCredentials: true,
                headers: this.getHeaders({ jwtToken }),
                throwOnError: false,
            });
            if (!response.success) {
                throw new Error(response.errorMessage);
            }
            else {
                return response.payload;
            }
        });
    }
    getSPLoginRequestConfig(jwtToken, config, serviceName) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const route = `/v1/auth/third-party-idp?serviceName=${serviceName}`;
            const response = yield this.httpPost(route, config, {
                withCredentials: true,
                headers: this.getHeaders({ jwtToken }),
                throwOnError: false,
            });
            return this.expectSuccess(response).payload;
        });
    }
    expectSuccess(response, message, errorType = Error) {
        if (response.success) {
            return response;
        }
        const errorMessage = message !== null && message !== void 0 ? message : response.errorMessage;
        throw new errorType(errorMessage);
    }
    refreshToken(jwtToken, refreshTokenId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!jwtToken) {
                throw new Error('JWT Token not provided.');
            }
            const opts = {
                withCredentials: true,
                headers: this.getHeaders({ jwtToken, refreshTokenId }),
                throwOnError: false,
            };
            const response = yield this.httpPost('/v1/auth/refresh', {}, opts);
            if (!response.success) {
                throw new errors_1.RefreshFailedError(response);
            }
            return response.payload.jwtToken;
        });
    }
    createExchangeToken(jwtToken, refreshTokenId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const opts = {
                withCredentials: true,
                headers: this.getHeaders({ jwtToken, refreshTokenId }),
                throwOnError: false,
            };
            const response = yield this.httpPost('/v1/auth/exchange', {}, opts);
            if (!response.success) {
                throw new errors_1.ExchangeFailedError(response);
            }
            return response.payload.exchangeToken;
        });
    }
    exchangeToken(exchangeToken, refreshTokenId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const opts = {
                withCredentials: true,
                headers: this.getHeaders({ refreshTokenId }),
                throwOnError: false,
            };
            const response = yield this.httpPost(`/v1/auth/exchange/${exchangeToken}/token`, {}, opts);
            if (!response.success) {
                throw new errors_1.ExchangeFailedError(response);
            }
            token_storage_1.TokenStorage.store(response.payload.jwtToken);
            return response.payload;
        });
    }
    getSamlLoginInfo(userSamlId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const opts = { responseCookies: new cookie_container_1.CookieContainer(), throwOnError: false };
            const response = yield this.httpGet(`/v1/auth/sso/get-user-infos/${userSamlId}`, opts);
            if (!response.success) {
                throw (0, errors_1.createAuthError)(response);
            }
            const cookie = opts.responseCookies.get('refreshtoken') || {};
            return Object.assign(Object.assign({}, response.payload), { refreshTokenId: cookie.value });
        });
    }
    getSamlLoginInfoV2(userSamlId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const opts = { responseCookies: new cookie_container_1.CookieContainer(), throwOnError: false };
            const response = yield this.httpGet(`/v1/auth/sso/get-user-infos/${userSamlId}`, opts);
            if (!response.success) {
                throw (0, errors_1.createAuthError)(response);
            }
            const cookie = opts.responseCookies.get('CS_SESSION_ID') || {};
            return Object.assign(Object.assign({}, response.payload), { sessionId: cookie.value });
        });
    }
    hasProvider(domain) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const opts = { responseCookies: new cookie_container_1.CookieContainer(), throwOnError: false };
            const response = yield this.httpGet(`/v1/auth/sso/has-provider?domain=${domain}`, opts);
            if (!response.success) {
                throw new Error();
            }
            return response.payload;
        });
    }
    getSsoRedirectUrl(domain, redirectTo, authVersion) {
        const config = this.settings;
        let url = `${config.url}/v1/auth/sso/login?domain=${domain}&redirectTo=${btoa(redirectTo)}`;
        if (authVersion) {
            url += `&version=${authVersion}`;
        }
        return url;
    }
    initiatePostBindingLogin(domain, redirectTo, authVersion) {
        const config = this.settings;
        let url = `${config.url}/v1/auth/sso/login/post?domain=${domain}&redirectTo=${btoa(redirectTo)}`;
        if (authVersion) {
            url += `&version=${authVersion}`;
        }
        return url;
    }
    logout(refreshTokenId, domain) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const opts = {
                throwOnError: false,
                withCredentials: true,
                headers: this.getHeaders({ refreshTokenId }),
            };
            const response = yield this.httpPost('/v1/auth/logout', { domain }, opts);
            if (response.success) {
                return response.payload;
            }
            return { result: logout_result_model_1.LogoutResultStatus.UnknownError };
        });
    }
    logoutFromIdentityProvider(domainName) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const response = yield this.httpGet(`/v1/auth/sso/logout?domain=${domainName}`);
            if (!response.success) {
                throw (0, errors_1.createAuthError)(response.payload);
            }
            return response.payload;
        });
    }
    getHeaders({ jwtToken, refreshTokenId }) {
        const rt = {};
        if (jwtToken) {
            rt.authorization = `Bearer ${jwtToken}`;
        }
        if (refreshTokenId) {
            rt.cookie = `${REFRESH_TOKEN}=${refreshTokenId}`;
        }
        return rt;
    }
    getGdprContent(locale) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const response = yield this.httpGet(`/v1/gdpr/${locale}`);
            return response.payload;
        });
    }
    requestPasswordReset(email) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const response = yield this.httpPost(`/v2/users/reset-password`, { email });
            return response.payload;
        });
    }
    resetPassword(email, password, token) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const response = yield this.httpPost(`/v2/users/change-password`, { email, password, token });
            return response.payload;
        });
    }
    verifyResetPasswordToken(email, token) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const response = yield this.httpPost(`/v2/users/reset-password/verify`, { email, token });
            return response.payload;
        });
    }
}
exports.AuthClient = AuthClient;
//# sourceMappingURL=auth.client.js.map